import React, { useEffect, useRef, useState } from "react";
import { Card, CardDescription, CardTitle, Input } from "campaign-builder/ui";
import { getEarliestLaunchDate, isLaunchDateOutdated, toPersistedLaunchDate } from "campaign-builder/state";
import { updateCampaign } from "graphQL/mutations/campaign";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useSaveStatus, trackSave } from "../saveStatus";
import { useLaunchDateFocus } from "../launchDateFocus";
import { useTargetingValidation, validateCampaignName, normalizeCampaignName } from "../targetingValidation";
import { RequiredLabel, FieldError } from "./fields";
import { LaunchDatePicker } from "./LaunchDatePicker";

const INVALID_DATE_MESSAGE = "Please select a valid date.";

export const CampaignDetailsCard = () => {
  const { campaignData, campaignId, updateCampaignState } = useCampaignData();
  const save = useSaveStatus();
  const { version: launchDateFocusVersion, consume: consumeLaunchDateFocus } = useLaunchDateFocus();
  const dateFieldRef = useRef<HTMLDivElement>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const name = campaignData?.campaignName ?? "";
  const selectedDate = campaignData?.startDate ? new Date(Number(campaignData.startDate)) : null;

  // Latest campaignData, so an in-flight name save reflects only the name back
  // onto current state instead of reverting fields edited during the request.
  const campaignDataRef = useRef(campaignData);
  campaignDataRef.current = campaignData;

  const { version: validationVersion } = useTargetingValidation();
  const lastValidation = useRef(0);

  useEffect(() => {
    if (isLaunchDateOutdated(campaignData?.startDate)) {
      setDateError(INVALID_DATE_MESSAGE);
    } else {
      // Clear a stale outdated-date error if the date became valid via an external
      // update (e.g. refetchCampaign). Only clears that specific message.
      setDateError((prev) => (prev === INVALID_DATE_MESSAGE ? null : prev));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!consumeLaunchDateFocus()) return;
    setDateError(INVALID_DATE_MESSAGE);
    const field = dateFieldRef.current;
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
    field?.querySelector("button")?.focus({ preventScroll: true });
  }, [launchDateFocusVersion, consumeLaunchDateFocus]);

  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    setNameError(validateCampaignName(name));
    if (!selectedDate) setDateError("Launch date is required");
    else if (isLaunchDateOutdated(campaignData?.startDate)) setDateError(INVALID_DATE_MESSAGE);
  }, [validationVersion, name, selectedDate]);

  const handleNameChange = (value: string) => {
    setNameError(validateCampaignName(value));
    if (campaignData) {
      updateCampaignState({ ...campaignData, campaignName: value });
    }
  };

  const handleNameBlur = () => {
    if (validateCampaignName(name) || !campaignId) return;
    const normalized = normalizeCampaignName(name);
    void trackSave(save, async () => {
      await updateCampaign({ campaignId, name: normalized });
      // Reflect the normalized name locally only after the save succeeds, so a
      // failed save doesn't leave the UI showing a value the DB never stored.
      // Merge onto the latest state (campaignDataRef), not the snapshot captured
      // when blur fired, so concurrent edits (e.g. launch date) aren't reverted.
      // Skip entirely if the name changed mid-save (user kept typing, or a newer
      // blur save raced ahead) so a stale request can't clobber the newer value.
      const latest = campaignDataRef.current;
      if (latest && latest.campaignName === name && normalized !== name) {
        updateCampaignState({ ...latest, campaignName: normalized });
      }
    });
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setDateError("Launch date is required");
      return;
    }
    setDateError(isLaunchDateOutdated(date) ? INVALID_DATE_MESSAGE : null);
    if (campaignData) {
      updateCampaignState({ ...campaignData, startDate: String(date.getTime()) });
    }
    if (campaignId) {
      void trackSave(save, () => updateCampaignExtraData({ campaignId, startDate: toPersistedLaunchDate(date) }));
    }
  };

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="details">
      <div className="flex flex-col gap-1">
        <CardTitle data-cypress-id="campaignDetailsTitle">Campaign Details</CardTitle>
        <CardDescription data-cypress-id="campaignDetailsBody">
          Start by setting a name for your campaign and selecting the start date.
        </CardDescription>
      </div>
      <div className="flex flex-col gap-1">
        <RequiredLabel data-cypress-id="campaignNameTitle">Campaign name</RequiredLabel>
        <Input
          value={name}
          onChange={(event) => handleNameChange(event.target.value)}
          onBlur={handleNameBlur}
          className={nameError ? "border-destructive" : undefined}
          data-cypress-id="campaignNameInput"
          data-cy="admCampaignNameInput"
          aria-invalid={!!nameError}
          aria-describedby={nameError ? "admCampaignNameInputError" : undefined}
        />
        <FieldError message={nameError} data-cypress-id="campaignNameInputError" id="admCampaignNameInputError" />
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-0.5">
          <RequiredLabel data-cypress-id="launchDateTitle">Launch date</RequiredLabel>
          <p className="text-muted-foreground text-xs leading-4 tracking-[-0.15px]">
            The date your campaign will be sent via USPS to start delivery
          </p>
        </div>
        <div ref={dateFieldRef}>
          <LaunchDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            minDate={getEarliestLaunchDate()}
            error={!!dateError}
            data-cypress-id="launchDatePicker"
            data-cy="admLaunchDateInput"
            aria-invalid={!!dateError}
            aria-describedby={dateError ? "admLaunchDateError" : undefined}
          />
        </div>
        <FieldError message={dateError} data-cypress-id="launchDateError" id="admLaunchDateError" />
      </div>
    </Card>
  );
};
